import dataUtils from '@/common/dataUtils';
import { ContainerType, MediaType, MODULE_ID, ROOT } from './constants';
import { performInMemorySearch } from '@/components/search/searchStore';
import { cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import moduleIndex from '@/modules/media';
import * as mediaService from '@/modules/media/services';
import { SYNC_STATES } from '@/store/sync/sync';

const state = {
  loading: true,
  containers: [],
  containersForSearch: [],
  containersById: {},
  containersByIdForSearch: {},
  medias: [],
  mediasById: {},
  tags: [],
  tagsById: {},
  userStatesById: {},
  unreadCountsByInstance: {},
  sorterName: '',
  cmsSorterName: '',
};

function mutateModuleNames(state, rootState) {
  for (const [_, moduleObj] of Object.entries(rootState.configs.moduleConfigsByModuleTypeAndInstance)) {
    if (moduleObj.type !== MediaType.MEDIA) continue;
    if (
      !state.containersById[moduleObj.instance] ||
      !state.containersById[moduleObj.instance].properties ||
      !state.containersById[moduleObj.instance].properties.name
    )
      continue;
    moduleObj.title = state.containersById[moduleObj.instance].properties.name;
  }
}

/** ------------------------------------------------------------------------------------------------------------------
 * Module Implementation for unread badge counts by instance.
 * IMPLEMENT this getter to enable unread counter badges
 * -------------------------------------------------------------------------------------------------------------------
 */
const getters = {
  getUnreadCountByModuleInstance: (state, _getters, rootState, _rootGetters) => (instance) => {
    mutateModuleNames(state, rootState);
    return state.unreadCountsByInstance[instance];
  },
};

const mutations = {
  setLoading(state, isLoading) {
    state.loading = isLoading;
  },

  setData(state, data) {
    state.containers = data.containers;
    state.medias = data.medias;
    state.tags = data.tags;
    state.userStatesById = dataUtils.buildIdMap(data.userStates, 'containerIdFk');
    state.containersById = dataUtils.buildIdMap(data.containers, 'id');
    state.containersForSearch = state.containers.filter((c) => !['FOLDER', 'ROW', 'COL'].includes(c.type));
    state.containersByIdForSearch = dataUtils.buildIdMap(data.containersForSearch, 'id');
    state.mediasById = dataUtils.buildIdMap(data.medias, 'id');
    state.tagsById = dataUtils.buildIdMap(data.tags, 'id');
  },

  extendData(state) {
    for (const c of state.containers) {
      if (c.type === ContainerType.FOLDER && !c.idParentFk) {
        extendContainer(state, c.id, c.id, '');
      }
    }
  },

  setModuleNames(state, rootState) {
    mutateModuleNames(state, rootState);
  },

  countModuleItems(state) {
    for (const container of state.containers) {
      state.unreadCountsByInstance[container.id] = 0;
      countUnread(state, container.id, container.id);
    }
  },

  setRead(state, containerId) {
    if (!state.containersById[containerId]) return;
    state.containersById[containerId].unread = false;
  },

  setConfirmation(state, containerId) {
    if (!state.containersById[containerId]) return;
    state.containersById[containerId].unconfirmed = false;
  },

  setFavorite(state, { containerId, value }) {
    if (!state.containersById[containerId]) return;
    state.containersById[containerId].favorite = value;
  },

  setSorterName(state, value) {
    state.sorterName = value;
  },
  setCmsSorterName(state, value) {
    state.cmsSorterName = value;
    if (!state.sorterName) {
      state.sorterName = value;
    }
  },
};
const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  async searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.containersForSearch,
      titleSearchAttr: '_title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  // TODO: DELETE as soon as nowhere used
  async updateModuleApplicationCache({ state }) {},

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    const files = state.medias
      .filter((media) => media.properties)
      .map((media) => {
        let { filename, checksum } = media.properties;
        return { filename, checksum };
      });
    await cacheAllModuleFiles({ files, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   *
   * Module Implementation for synchronizing module data
   * Each module MUST IMPLEMENT this action and take responsibility for the following actions:
   * - load module meta data from network (service-worker makes sure these data is always (if online) loaded from network.
   * - if file metadata loaded, these records need to be enhanced with the flag _offline if the file is in application cache
   *   The function getLocalStorageModuleFileState in cachingUtils.js does this together with addCachedState automatically
   * - prepare the received data for storing in vuex store (e.g. create id maps if you need to access the data by id later on)
   * - call a setData mutation to store these data items in module store. Be sure to freeze Objects with Object.freeze(data)
   *   before storing in vuex store if and only if these data items doesn't need to be reactive (performance gain!!!)
   * - After pre-processing of those module data, all documents have to be cached and
   * - after caching, the application cache and the local storage needs to be updated
   */
  async synchronizeData({ commit, rootState }) {
    commit('setLoading', true);
    try {
      const { data } = await mediaService.getAllContainers();
      commit('setData', data);
      commit('extendData');
      commit('setModuleNames', rootState);
    } catch (error) {
      console.error(error);
      commit('sync/setSyncState', { moduleId: MODULE_ID, syncState: SYNC_STATES.FAILURE, msg: error }, ROOT);
      throw error;
    }
    commit('setLoading', false);
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

function extendContainer(state, moduleId, containerId, path) {
  if (!state.containersById[containerId]) return;

  const container = state.containersById[containerId];
  if (container.type === ContainerType.FOLDER && container.idParentFk) {
    path = path === '' ? container.properties.name : `${path}/${container.properties.name}`;
  }

  container.moduleId = moduleId;
  container.folderId = container.idParentFk || moduleId;
  container._moduleTitle = state.containersById[moduleId].properties.name;
  container._title = container.properties.name;
  container._displayLocation = path;
  container._path = `/media/${moduleId}/${container.folderId}/${container.id}`;

  if (container.unread) {
    state.unreadCountsByInstance[moduleId] = (state.unreadCountsByInstance[moduleId] || 0) + 1;
  }

  container._userState = state.userStatesById[containerId] || {
    proto: true,
    containerIdFk: containerId,
    favorite: 0,
    dateread: null,
    dateconfirmed: null,
    userdatemodified: null,
  };
  container._userState._type = container.type;

  if (container.type === ContainerType.FOLDER && container.children) {
    for (const id of container.children) {
      extendContainer(state, moduleId, id, path);
    }
  }
}

function countUnread(state, moduleInstance, containerId) {
  if (!state.containersById[containerId]) return;

  const container = state.containersById[containerId];

  if (container.unread) {
    state.unreadCountsByInstance[moduleInstance] = (state.unreadCountsByInstance[moduleInstance] || 0) + 1;
  }

  if (container.children) {
    for (const id of container.children) {
      if (container.type === 'FOLDER') {
        countUnread(state, moduleInstance, id);
      }
    }
  }
}
